/* sugar:assets/bundle-builder.js@2.3.2 */
/*
 * Bundle Builder — tap rows, the tier follows the count, one button adds every line.
 *
 * Markup contract (blocks/bundle-builder.liquid, snippets/bundle-item-card.liquid):
 *   - `[data-bundle-builder]` root with the settings it needs as data attributes
 *   - `[data-bb-variants]` JSON: id, price, compare, available, options, plans{id:{price,compare}}
 *   - `.sugar-bundle-tier[data-bundle-tier]` rows rendered inline by the children; lifted into
 *     `[data-bb-tiers]` here, because a parent block cannot sort its children by type
 *   - `[data-bundle-item]` rows with `[data-bb-add]`, `[data-bb-minus]`, `[data-bb-plus]`, `[data-bb-count]`,
 *     `[data-bb-row-price]` / `[data-bb-row-compare]` (repriced at the tier when `data-prices="true"`),
 *     and on a hand-picked row with Variant dropdown on a `[data-bb-variant]` select whose options carry
 *     each variant's id, price, compare, available, inventory and plans
 *   - `data-picker="included"` (Always included): every row counts once, with no control, no
 *     minimum, no offers pairing and the bar kept hidden; the tiers still price the lines
 *   - the page's Product Buy Button adds the lines: the engine hooks the next cart form after the
 *     root, swaps its text below the minimum, and writes the bundle's totals on the root
 *     (`data-bb-count/total/compare/onetime-total/onetime-compare`) for sugar-pricing.js
 *   - events out: `bundle:changed` {count, tier, total, sectionId}
 *   - events in: `variant:change`, `subscription:type-changed`
 *   - Offers mode: with no Bundle Tier children and an Offer List / Offer Cards in the section, the
 *     rows and the offers keep each other in step — picking rows moves the offer's quantity, picking
 *     an offer adds to or trims the last-picked rows — and Offers owns the form, the prices and the
 *     gifts, reading the row split from `data-bb-lines` on the root
 *   - Inside a Step 2 block (`[data-step-two]`, assets/step-two.js) the rows start empty and the
 *     count picked in step 1 (`step-two:open`) is the minimum: the button after the rows says what is
 *     missing until it is reached (`data-bb-short` on the root), picks above it move the offer up,
 *     and no pick takes the offer below it
 *   - One row per unit (`data-bb-units`): every `[data-bb-unit]` card is one unit with a dropdown, or a
 *     radio group of swatches or buttons, per product option (`[data-bb-unit-option]`, a select or a
 *     fieldset), resolved to a variant here; the count is the number of cards. The step's count (or
 *     the offer, outside a step) sets how many there are, and `[data-bb-add-unit]` adds one (cloned
 *     from `[data-bb-unit-tpl]`, copying the last card's picks) up to Maximum items, which moves the
 *     offer up. A card on a sold-out combination counts 0. `[data-bb-unit-num]` holds the card's
 *     number and `[data-bb-unit-picks]` its picked values; in the Add another card,
 *     `[data-bb-count-text]` groups show the entry keyed by the number of cards and `[data-bb-pct]`
 *     takes the saving one offer up
 *   - Every Buy Button in the section that sells the builder's product adds the bundle (a second
 *     button such as a pre-order one adds the same lines on its own plan), and each one's text
 *     gives way to the minimum text until the count reaches it
 */
(function () {
  'use strict';
  if (window.SugarBundleBuilder) return;

  /* ---------------------------------------------------------------- money */

  function group(amount, precision, thousands, decimal) {
    const fixed = Math.abs(amount).toFixed(precision);
    const parts = fixed.split('.');
    const whole = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
    const sign = amount < 0 ? '-' : '';
    return sign + (parts[1] ? whole + decimal + parts[1] : whole);
  }

  function formatMoney(cents, format) {
    const amount = Number(cents) / 100;
    if (!Number.isFinite(amount)) return '';
    const template = format || '${{amount}}';
    const match = template.match(/\{\{\s*(\w+)\s*\}\}/);
    if (!match) return template;
    let out;
    switch (match[1]) {
      case 'amount_no_decimals': out = group(amount, 0, ',', '.'); break;
      case 'amount_with_comma_separator': out = group(amount, 2, '.', ','); break;
      case 'amount_no_decimals_with_comma_separator': out = group(amount, 0, '.', ','); break;
      case 'amount_with_apostrophe_separator': out = group(amount, 2, "'", '.'); break;
      case 'amount_with_space_separator': out = group(amount, 2, ' ', ','); break;
      case 'amount_no_decimals_with_space_separator': out = group(amount, 0, ' ', ','); break;
      default: out = group(amount, 2, ',', '.');
    }
    return template.replace(match[0], out);
  }

  function fill(template, values) {
    let out = String(template || '');
    Object.keys(values).forEach((key) => {
      out = out.split('[' + key + ']').join(values[key]);
    });
    return plural(out);
  }

  /* [one|many] after a number: the singular when the number just before it is 1 (Select at least 1 bag / 2 bags). */
  function plural(text) {
    return String(text || '').replace(/\[([^\[\]|<>]*)\|([^\[\]|<>]*)\]/g, (m, one, many, offset, whole) => {
      const nums = whole.slice(0, offset).replace(/<[^>]*>/g, ' ').match(/\d+(?:[.,]\d+)*/g);
      return nums && nums[nums.length - 1] === '1' ? one : many;
    });
  }

  function decodeEntities(text) {
    const el = document.createElement('textarea');
    el.innerHTML = text || '';
    return el.value;
  }

  const reduceMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ---------------------------------------------------------------- builder */

  class BundleBuilder {
    constructor(root) {
      this.root = root;
      this.sectionId = root.dataset.sectionId || '';
      this.list = root.querySelector('[data-bb-list]');
      this.tiersEl = root.querySelector('[data-bb-tiers]');
      this.fillEl = root.querySelector('[data-bb-fill]');
      this.headerEl = root.querySelector('[data-bb-header]');
      this.moreBtn = root.querySelector('[data-bb-more]');
      this.errorEl = root.querySelector('[data-bb-error]');
      this.emptyEl = root.querySelector('[data-bb-empty]');
      this.moneyFormat = decodeEntities(root.dataset.moneyFormat || '');
      this.headerTpl = decodeEntities(root.dataset.headerTemplate || '');
      this.buttonMinTpl = decodeEntities(root.dataset.buttonMinTemplate || '');
      this.animateDot = root.dataset.animateDot === 'true';
      this.lowStock = parseInt(root.dataset.lowStock, 10) || 0;
      this.visibleItems = parseInt(root.dataset.visibleItems, 10) || 0;
      this.max = parseInt(root.dataset.max, 10) || 0;
      this.min = Math.max(1, parseInt(root.dataset.min, 10) || 1);
      this.currentVariantId = String(root.dataset.currentVariantId || '');
      this.included = root.dataset.picker === 'included';
      this.stepRoot = root.closest('[data-step-two]');
      this.stepMode = !!this.stepRoot && !this.included;
      this.showPrices = root.dataset.prices === 'true';
      this.unitsMode = root.hasAttribute('data-bb-units');
      this.unitTpl = root.querySelector('[data-bb-unit-tpl]');
      this.addUnitBtn = root.querySelector('[data-bb-add-unit]');
      this.unitSeq = 0;
      this.unitImages = {};
      try {
        const images = root.querySelector('[data-bb-unit-images]');
        this.unitImages = images ? JSON.parse(images.textContent || '{}') : {};
      } catch (e) {
        this.unitImages = {};
      }
      this.pagePlanId = null;
      this.lastActiveIndex = -1;
      this.listeners = [];

      this.baseMin = this.min;
      const urlMin = parseInt(new URLSearchParams(window.location.search).get('bundle'), 10);
      if (Number.isFinite(urlMin) && urlMin > this.min) this.min = urlMin;
      if (this.max && this.min > this.max) this.max = this.min;
      /* Always included: the rows are the bundle, so there's no minimum to reach or cap to hit */
      if (this.included) {
        this.min = 1;
        this.max = 0;
      }

      this.variants = [];
      this.variantsById = new Map();
      try {
        const json = root.querySelector('[data-bb-variants]');
        this.variants = json ? JSON.parse(json.textContent || '[]') : [];
      } catch (e) {
        this.variants = [];
      }
      this.variants.forEach((v) => this.variantsById.set(String(v.id), v));

      const pageState = window.subscriptionStateBySection && window.subscriptionStateBySection[this.sectionId];
      if (pageState && pageState.type === 'subscribe' && pageState.sellingPlanId) this.pagePlanId = String(pageState.sellingPlanId);

      this.pickOrder = [];
      this.liftTiers();
      const section = root.closest('.shopify-section');
      this.offersRoot = section ? section.querySelector('[data-sugar-offers]') : null;
      this.offersMode = !this.included && this.tiers.length === 0 && !!this.offersRoot;
      root.dataset.bbMode = this.offersMode ? 'offers' : 'tiers';
      if (!this.offersMode) this.bindForm();
      else if (this.stepMode) this.bindStepForm();
      this.bindItems();
      this.bindDocument();
      this.applyStock();
      if (this.included) this.items().forEach((item) => this.setCount(item, 1, true));
      /* Start in the bundle: a row the server drew picked counts once, ahead of any pick the offers add */
      else this.items().forEach((item) => { if (item.hasAttribute('data-bb-start')) this.setCount(item, 1, true); });
      this.setupScroll();
      if (this.offersMode && !this.stepMode) this.fillFromOffers(true);
      this.update(true);
    }

    /* ------------------------------------------------------------ offers mode */

    offersController() {
      const controller = this.offersRoot && this.offersRoot.sugarOffers;
      return controller && typeof controller.setRequestedQuantity === 'function' ? controller : null;
    }

    offersQuantity() {
      const selected = this.offersRoot && this.offersRoot.querySelector('.offer.is-selected');
      if (!selected) return 0;
      const requested = parseInt(selected.dataset.computedRequestedQuantity, 10);
      return Number.isFinite(requested) && requested > 0 ? requested : (parseInt(selected.dataset.quantity, 10) || 1);
    }

    /* Bring the rows up or down to the offer's quantity: more goes onto the last-picked row, less comes off it. */
    fillFromOffers(initial, target) {
      const want = Number.isFinite(target) ? target : this.offersQuantity();
      if (!(want > 0)) return;
      if (this.unitsMode) {
        this.setUnits(want);
        if (!initial) this.update(false);
        return;
      }
      let guard = 64;
      while (this.total() < want && guard--) {
        const last = this.pickOrder[this.pickOrder.length - 1];
        const row = (last && this.canAdd(last)) ? last : this.items().find((item) => this.canAdd(item));
        if (!row) break;
        this.setCount(row, this.countOf(row) + 1, true);
      }
      guard = 64;
      while (this.total() > want && guard--) {
        const last = this.pickOrder[this.pickOrder.length - 1];
        if (!last) break;
        this.setCount(last, this.countOf(last) - 1, true);
      }
      if (!initial) this.update(false);
    }

    canAdd(item) {
      if (item.dataset.available === 'false' || item.dataset.stock === 'out') return false;
      return !(this.max > 0 && this.total() >= this.max);
    }

    syncToOffers() {
      const controller = this.offersController();
      /* In a step, the count picked in step 1 is the floor: fewer picks leave the offer there. */
      const n = this.stepMode ? Math.max(this.total(), this.min) : this.total();
      if (!controller || !(n > 0)) return;
      if (this.offersQuantity() === n) return;
      this.syncing = true;
      try {
        controller.setRequestedQuantity(n);
      } finally {
        this.syncing = false;
      }
    }

    listen(target, type, handler, options) {
      target.addEventListener(type, handler, options);
      this.listeners.push(() => target.removeEventListener(type, handler, options));
    }

    destroy() {
      this.listeners.forEach((off) => off());
      this.listeners = [];
    }

    /* ------------------------------------------------------------ tiers */

    liftTiers() {
      const found = Array.from(this.root.querySelectorAll('.sugar-bundle-tier'));
      this.tiers = found
        .map((el) => ({
          el,
          qty: Math.max(1, parseInt(el.dataset.qty, 10) || 1),
          discountType: el.dataset.discountType || 'none',
          discountValue: parseFloat(el.dataset.discountValue) || 0,
          planId: el.dataset.planId || '',
          priceTpl: decodeEntities(el.dataset.priceTemplate || ''),
          compareTpl: decodeEntities(el.dataset.compareTemplate || '')
        }))
        .sort((a, b) => a.qty - b.qty);
      if (this.tiersEl) {
        this.tiers.forEach((tier) => this.tiersEl.appendChild(tier.el));
        this.root.style.setProperty('--bb-tiers', String(Math.max(this.tiers.length, 1)));
      }
      // Shopify wraps each child in a div; a wrapper left empty by the lift would still take a gap
      if (this.list) {
        Array.from(this.list.children).forEach((child) => {
          if (child.hasAttribute('data-bundle-item')) return;
          if (!child.querySelector('[data-bundle-item]')) child.hidden = true;
        });
      }
      const bar = this.root.querySelector('.sugar-bundle-builder__bar');
      if (bar) bar.hidden = this.tiers.length === 0 || this.included;
      if (this.emptyEl) this.emptyEl.hidden = !(this.tiers.length === 0 && this.items().length === 0);
    }

    items() {
      return Array.from(this.list ? this.list.querySelectorAll('[data-bundle-item]') : []);
    }

    countOf(item) {
      return parseInt(item.dataset.count, 10) || 0;
    }

    total() {
      return this.items().reduce((sum, item) => sum + this.countOf(item), 0);
    }

    activeIndex(n) {
      let index = -1;
      this.tiers.forEach((tier, i) => {
        if (tier.qty <= n) index = i;
      });
      return index;
    }

    progress(n) {
      const N = this.tiers.length;
      if (!N || n <= 0) return 0;
      const i = this.activeIndex(n);
      if (i < 0) return 0;
      if (N === 1 || i === N - 1) return 1;
      // A reached stop fills half the segment beyond it, so tier 1 of 3 already shows a quarter bar;
      // extra units between stops fill the rest of that segment.
      const span = this.tiers[i + 1].qty - this.tiers[i].qty;
      const within = span > 0 ? (n - this.tiers[i].qty) / span : 0;
      return (i + 0.5 + Math.min(within, 0.98) * 0.5) / (N - 1);
    }

    /* ------------------------------------------------------------ prices */

    unitPrice(variant, tier, withPlan = true, ownPlanId = null) {
      if (!variant) return 0;
      let base = Number(variant.price) || 0;
      const planId = ownPlanId || (withPlan ? ((tier && tier.planId) || this.pagePlanId) : null);
      if (planId && variant.plans && variant.plans[planId]) base = Number(variant.plans[planId].price) || base;
      if (!tier) return base;
      const value = tier.discountValue;
      /* Per unit, the part of a cent dropped from the discount, the way checkout charges it. */
      if (tier.discountType === 'percentage' && value > 0) return Math.max(0, base - Math.floor((base * Math.round(value * 100)) / 10000));
      if (tier.discountType === 'fixed' && value > 0) return Math.max(0, base - Math.round(value * 100));
      if (tier.discountType === 'set_price' && value > 0) return Math.round(value * 100);
      return base;
    }

    unitCompare(variant, unit) {
      if (!variant) return 0;
      const compare = Math.max(Number(variant.compare) || 0, Number(variant.price) || 0);
      return compare > unit ? compare : 0;
    }

    referenceVariant() {
      return this.variantsById.get(this.currentVariantId) || this.variants[0] || null;
    }

    rowVariant(item) {
      const plans = {};
      String(item.dataset.plans || '').split('|').forEach((entry) => {
        const parts = entry.split(':');
        if (parts.length >= 2 && parts[0]) plans[parts[0]] = { price: Number(parts[1]) || 0, compare: Number(parts[2]) || 0 };
      });
      return {
        id: String(item.dataset.variantId || ''),
        price: Number(item.dataset.price) || 0,
        compare: Number(item.dataset.compare) || 0,
        available: item.dataset.available !== 'false',
        options: [],
        plans
      };
    }

    variantOf(item) {
      const optionIndex = item.dataset.optionIndex;
      if (optionIndex === undefined || optionIndex === '') return this.variantsById.get(String(item.dataset.variantId)) || this.rowVariant(item);
      const idx = parseInt(optionIndex, 10);
      const value = item.dataset.optionValue;
      const current = this.referenceVariant();
      const candidates = this.variants.filter((v) => Array.isArray(v.options) && v.options[idx] === value);
      if (!candidates.length) return this.variantsById.get(String(item.dataset.variantId)) || null;
      const matching = current
        ? candidates.find((v) => v.options.every((opt, j) => j === idx || opt === current.options[j]))
        : null;
      return matching || candidates.find((v) => v.available) || candidates[0];
    }

    money(cents) {
      return formatMoney(cents, this.moneyFormat);
    }

    tierValues(tier, variant) {
      const unit = this.unitPrice(variant, tier);
      const compare = this.unitCompare(variant, unit);
      const saved = compare > 0 ? Math.round((compare - unit) * 100 / compare) : 0;
      return {
        unit,
        compare,
        values: {
          price: this.money(unit),
          compare_price: compare > 0 ? this.money(compare) : '',
          total: this.money(unit * tier.qty),
          compare_total: compare > 0 ? this.money(compare * tier.qty) : '',
          percentage: String(saved),
          quantity: String(tier.qty)
        }
      };
    }

    /* ------------------------------------------------------------ render */

    update(initial) {
      const n = this.total();
      const active = this.activeIndex(n);
      const tier = active >= 0 ? this.tiers[active] : null;
      const reference = this.referenceVariant();

      this.tiers.forEach((t, i) => {
        const { compare, values } = this.tierValues(t, reference);
        const priceEl = t.el.querySelector('[data-tier-price]');
        const compareEl = t.el.querySelector('[data-tier-compare]');
        if (priceEl) priceEl.innerHTML = fill(t.priceTpl, values);
        if (compareEl) {
          compareEl.innerHTML = compare > 0 ? fill(t.compareTpl, values) : '';
          compareEl.hidden = !(compare > 0 && t.compareTpl);
        }
        t.el.classList.toggle('is-reached', i <= active);
        t.el.classList.toggle('is-active', i === active);
        if (i !== active) t.el.classList.remove('is-popping');
        else if (!initial && this.animateDot && !reduceMotion && active !== this.lastActiveIndex) {
          t.el.classList.remove('is-popping');
          void t.el.offsetWidth;
          t.el.classList.add('is-popping');
        }
      });
      this.lastActiveIndex = active;
      this.root.style.setProperty('--bb-progress', String(this.progress(n)));

      const remaining = Math.max(0, this.min - n);
      const headerValues = { count: String(n), min: String(this.min), remaining: String(remaining) };
      if (this.headerEl) this.headerEl.innerHTML = fill(this.headerTpl, headerValues);

      let total = 0;
      let compareTotal = 0;
      let oneTimeTotal = 0;
      let oneTimeCompare = 0;
      this.items().forEach((item) => {
        const qty = this.countOf(item);
        if (!qty) return;
        const variant = this.variantOf(item);
        const unit = this.unitPrice(variant, tier);
        const compare = this.unitCompare(variant, unit) || unit;
        const oneTime = this.unitPrice(variant, tier, false);
        total += unit * qty;
        compareTotal += compare * qty;
        oneTimeTotal += oneTime * qty;
        oneTimeCompare += (this.unitCompare(variant, oneTime) || oneTime) * qty;
      });

      if (this.showPrices) this.items().forEach((item) => this.paintPrice(item, tier));

      const atMax = this.max > 0 && n >= this.max;
      this.items().forEach((item) => {
        const plus = item.querySelector('[data-bb-plus]');
        const add = item.querySelector('[data-bb-add]');
        const minus = item.querySelector('[data-bb-minus]');
        const soldOut = item.dataset.available === 'false' || item.dataset.stock === 'out';
        if (plus) plus.disabled = soldOut || atMax;
        if (add) add.disabled = soldOut || atMax;
        if (minus) minus.disabled = this.offersMode && !this.stepMode && n <= 1 && this.countOf(item) === 1;
      });

      if (this.unitsMode) this.numberUnits();
      if (this.addUnitBtn) this.paintAddUnit(n, atMax);

      if (!initial) this.root.dataset.bbUpdated = String(Date.now());
      if (this.offersMode) {
        this.root.dataset.bbLines = JSON.stringify(this.cartItems().map((line) => ({ id: line.id, quantity: line.quantity })));
        if (this.stepMode) {
          const short = n < this.min ? 'true' : 'false';
          if (this.root.dataset.bbShort !== short) this.root.dataset.bbShort = short;
          this.syncForm(n, headerValues);
        }
      } else {
        this.root.dataset.bbCount = String(n);
        this.root.dataset.bbTotal = String(total);
        this.root.dataset.bbCompare = String(compareTotal);
        this.root.dataset.bbOnetimeTotal = String(oneTimeTotal);
        this.root.dataset.bbOnetimeCompare = String(oneTimeCompare);
        this.syncForm(n, headerValues);
      }

      document.dispatchEvent(new CustomEvent('bundle:changed', {
        detail: { count: n, tier: tier ? tier.qty : 0, total, sectionId: this.sectionId, root: this.root }
      }));
    }

    /* Row prices: one unit of the row at the active tier, its full price struck through when higher.
       Written only when the text changes. */
    paintPrice(item, tier) {
      const priceEl = item.querySelector('[data-bb-row-price]');
      if (!priceEl) return;
      const variant = this.variantOf(item);
      const unit = this.unitPrice(variant, tier);
      const compare = this.unitCompare(variant, unit);
      const price = this.money(unit);
      if (priceEl.textContent !== price) priceEl.textContent = price;
      const compareEl = item.querySelector('[data-bb-row-compare]');
      if (!compareEl) return;
      const was = compare > 0 ? this.money(compare) : '';
      if (compareEl.textContent !== was) compareEl.textContent = was;
      const struck = compareEl.closest('s');
      if (struck && struck.hidden !== !was) struck.hidden = !was;
    }

    setCount(item, next, silent) {
      const current = this.countOf(item);
      const clamped = Math.max(0, next);
      const delta = clamped - current;
      if (!delta) return;
      if (delta > 0 && this.max > 0 && this.total() + delta > this.max) return;
      if (delta > 0 && (item.dataset.available === 'false' || item.dataset.stock === 'out')) return;
      /* Offers mode always holds a pick: the offer it mirrors can't be "none" (a step may hold none) */
      if (delta < 0 && this.offersMode && !this.stepMode && !silent && this.total() + delta < 1) return;
      item.dataset.count = String(clamped);
      const countEl = item.querySelector('[data-bb-count]');
      if (countEl) countEl.textContent = String(clamped);
      item.classList.toggle('is-selected', clamped > 0);
      if (delta > 0) {
        for (let i = 0; i < delta; i++) this.pickOrder.push(item);
      } else {
        for (let i = 0; i < -delta; i++) {
          const at = this.pickOrder.lastIndexOf(item);
          if (at > -1) this.pickOrder.splice(at, 1);
        }
      }
      if (silent) return;
      this.update(false);
      if (this.offersMode) this.syncToOffers();
    }

    /* ------------------------------------------------------------ stock */

    applyStock(items = this.items()) {
      items.forEach((item) => {
        const available = item.dataset.available !== 'false';
        const inventory = parseInt(item.dataset.inventory, 10);
        if (!available) item.dataset.stock = 'out';
        else if (this.lowStock > 0 && Number.isFinite(inventory) && inventory < 9999 && inventory > 0 && inventory < this.lowStock) item.dataset.stock = 'low';
        else item.dataset.stock = 'ok';
        const add = item.querySelector('[data-bb-add]');
        if (add) add.setAttribute('aria-label', (this.root.dataset.addText || 'Add') + ' ' + (item.dataset.title || ''));
      });
    }

    /* ------------------------------------------------------------ scroll */

    setupScroll() {
      if (!this.visibleItems || !this.list) return;
      const items = this.items();
      if (items.length <= this.visibleItems) return;
      const measure = () => {
        const gap = parseFloat(getComputedStyle(this.list).rowGap) || 0;
        let height = 0;
        for (let i = 0; i < this.visibleItems; i++) height += items[i].offsetHeight + (i ? gap : 0);
        height += gap + items[this.visibleItems].offsetHeight * 0.5;
        this.list.style.maxHeight = Math.round(height) + 'px';
        this.list.classList.add('is-scrollable');
        if (this.moreBtn) this.moreBtn.hidden = false;
        this.syncMore();
      };
      measure();
      this.listen(window, 'resize', measure);
      this.list.querySelectorAll('img').forEach((img) => {
        if (!img.complete) this.listen(img, 'load', measure);
      });
      this.listen(this.list, 'scroll', () => this.syncMore(), { passive: true });
      if (this.moreBtn) {
        this.listen(this.moreBtn, 'click', () => {
          this.list.scrollBy({ top: this.list.clientHeight * 0.8, behavior: reduceMotion ? 'auto' : 'smooth' });
        });
      }
    }

    syncMore() {
      if (!this.moreBtn || !this.list) return;
      const atEnd = this.list.scrollTop + this.list.clientHeight >= this.list.scrollHeight - 4;
      this.moreBtn.dataset.atEnd = atEnd ? 'true' : 'false';
    }

    /* ------------------------------------------------------------ events */

    bindItems() {
      if (!this.list) return;
      this.listen(this.list, 'click', (e) => {
        const add = e.target.closest('[data-bb-add]');
        const plus = e.target.closest('[data-bb-plus]');
        const minus = e.target.closest('[data-bb-minus]');
        const control = add || plus || minus;
        if (!control) return;
        const item = control.closest('[data-bundle-item]');
        if (!item || !this.list.contains(item)) return;
        e.preventDefault();
        if (minus) this.setCount(item, this.countOf(item) - 1);
        else this.setCount(item, this.countOf(item) + 1);
      });
      this.listen(this.list, 'change', (e) => {
        const unitSelect = e.target.closest('[data-bb-unit-option]');
        if (unitSelect) {
          const unit = unitSelect.closest('[data-bb-unit]');
          if (unit && this.list.contains(unit)) this.pickUnit(unit);
          return;
        }
        const select = e.target.closest('[data-bb-variant]');
        const item = select && select.closest('[data-bundle-item]');
        if (item && this.list.contains(item)) this.pickVariant(item, select);
      });
      if (this.addUnitBtn) {
        this.listen(this.addUnitBtn, 'click', (e) => {
          e.preventDefault();
          if (this.max > 0 && this.units().length >= this.max) return;
          const card = this.addUnit();
          if (!card) return;
          this.update(false);
          if (this.offersMode) this.syncToOffers();
        });
      }
    }

    /* ------------------------------------------------------------ one row per unit */

    units() {
      return Array.from(this.list ? this.list.querySelectorAll('[data-bb-unit]') : []);
    }

    /* An option's picked value: a dropdown's value, or the checked radio in a swatch or button group */
    optionValue(el) {
      if (el.tagName === 'SELECT') return el.value;
      const checked = el.querySelector('input:checked');
      return checked ? checked.value : '';
    }

    setOptionValue(el, value) {
      if (el.tagName === 'SELECT') {
        el.value = value;
        return;
      }
      el.querySelectorAll('input[type="radio"]').forEach((input) => {
        input.checked = input.value === value;
      });
    }

    picksOf(card) {
      return Array.from(card.querySelectorAll('[data-bb-unit-option]')).map((el) => this.optionValue(el));
    }

    /* Each card's number, where its label says [index]; written only when it changed */
    numberUnits() {
      this.units().forEach((card, i) => {
        const text = String(i + 1);
        card.querySelectorAll('[data-bb-unit-num]').forEach((el) => {
          if (el.textContent !== text) el.textContent = text;
        });
      });
    }

    /* A new card after the last one, on the last card's picks (or the template's, with none) */
    addUnit() {
      if (!this.unitTpl) return null;
      const cards = this.units();
      const last = cards[cards.length - 1];
      const card = this.unitTpl.content.firstElementChild.cloneNode(true);
      card.removeAttribute('data-bb-start');
      card.dataset.count = '0';
      /* Radio groups are named per card, or every copy would share one group */
      this.unitSeq += 1;
      const uid = this.root.getAttribute('data-_sf') || this.sectionId;
      card.querySelectorAll('[data-bb-unit-option]').forEach((group) => {
        group.querySelectorAll('input[type="radio"]').forEach((input) => {
          input.name = 'bb-' + uid + '-c' + this.unitSeq + '-' + group.dataset.bbUnitOption;
        });
      });
      if (last) {
        last.after(card);
        const picks = this.picksOf(last);
        card.querySelectorAll('[data-bb-unit-option]').forEach((el, i) => {
          if (picks[i] !== undefined) this.setOptionValue(el, picks[i]);
        });
      } else {
        this.list.insertBefore(card, this.list.firstChild);
      }
      this.pickUnit(card, true);
      return card;
    }

    /* Exactly n cards: added on the last card's picks, or taken off the end */
    setUnits(n) {
      const want = Math.max(this.included ? 0 : 1, parseInt(n, 10) || 0);
      let cards = this.units();
      let guard = 64;
      while (cards.length < want && guard--) {
        if (!this.addUnit()) break;
        cards = this.units();
      }
      while (cards.length > want) {
        const card = cards.pop();
        this.setCount(card, 0, true);
        card.remove();
      }
    }

    /* The card's dropdowns name a variant: the card becomes that line, its picture follows, and a
       combination that is sold out or doesn't exist counts 0 until another is picked. */
    pickUnit(card, silent) {
      const picks = this.picksOf(card);
      const picksEl = card.querySelector('[data-bb-unit-picks]');
      if (picksEl) {
        const text = picks.join(' / ');
        if (picksEl.textContent !== text) picksEl.textContent = text;
      }
      const variant = picks.length
        ? this.variants.find((v) => Array.isArray(v.options) && picks.every((value, i) => v.options[i] === value))
        : this.variantsById.get(String(card.dataset.variantId));
      const available = !!(variant && variant.available);
      if (variant) {
        card.dataset.variantId = String(variant.id);
        card.dataset.price = String(variant.price);
        card.dataset.compare = String(Number(variant.compare) > Number(variant.price) ? variant.compare : 0);
        const src = this.unitImages[String(variant.id)];
        const img = card.querySelector('[data-bb-unit-img]');
        if (img && src && img.getAttribute('src') !== src) {
          img.removeAttribute('srcset');
          img.src = src;
        }
      }
      card.dataset.available = available ? 'true' : 'false';
      card.dataset.inventory = available ? '9999' : '0';
      card.classList.toggle('is-soldout', !available);
      this.applyStock([card]);
      this.setCount(card, available ? 1 : 0, true);
      if (!silent) {
        this.update(false);
        if (this.offersMode) this.syncToOffers();
        // A unit card picks a variant without changing the main one; the gallery still shows its picture.
        // Deferred so it lands after the offers' own deferred re-announcement of slot 1.
        if (variant) {
          setTimeout(() => {
            document.dispatchEvent(new CustomEvent('offers:slot-variant-change', {
              detail: { sectionId: this.sectionId, variant }
            }));
          }, 60);
        }
      }
    }

    /* Add another: the next unit's number, the words for this many cards, the saving one offer up,
       hidden at Maximum items */
    paintAddUnit(n, atMax) {
      const cards = this.units().length;
      const hide = this.max > 0 && cards >= this.max;
      if (this.addUnitBtn.hidden !== hide) this.addUnitBtn.hidden = hide;
      const num = this.addUnitBtn.querySelector('[data-bb-add-unit-num]');
      if (num && num.textContent !== String(cards + 1)) num.textContent = String(cards + 1);
      /* [tier]1: …|2: …[/tier]: the entry keyed by the number of cards; with none, the text showing stays */
      this.addUnitBtn.querySelectorAll('[data-bb-count-text]').forEach((group) => {
        const entries = Array.from(group.children).filter((el) => el.hasAttribute('data-tier'));
        const match = entries.find((el) => parseInt(String(el.getAttribute('data-tier')).replace('#', ''), 10) === cards);
        if (!match) return;
        entries.forEach((el) => {
          const hidden = el !== match;
          if (el.hidden !== hidden) el.hidden = hidden;
        });
      });
      const slots = this.addUnitBtn.querySelectorAll('[data-bb-pct]');
      if (!slots.length) return;
      let pct = '';
      const next = this.offersRoot && Array.from(this.offersRoot.querySelectorAll('.offer'))
        .find((offer) => (parseInt(offer.dataset.quantity, 10) || 0) === cards + 1);
      if (next) {
        const total = parseInt(next.dataset.computedTotalPrice, 10);
        const compare = parseInt(next.dataset.computedComparePrice || next.dataset.comparePrice, 10);
        if (total > 0 && compare > total) pct = Math.round((1 - total / compare) * 100) + '%';
      }
      slots.forEach((slot) => {
        if (slot.textContent !== pct) slot.textContent = pct;
      });
    }

    /* A row's own variant dropdown: the picked option carries that variant's id, price, compare
       price, stock and plans, so the row becomes that line and the totals follow. Its units stay;
       an Always included row drops out while its variant is sold out. */
    pickVariant(item, select) {
      const option = select.options[select.selectedIndex];
      if (!option) return;
      item.dataset.variantId = option.value;
      item.dataset.price = option.dataset.price || '0';
      item.dataset.compare = option.dataset.compare || '0';
      item.dataset.available = option.dataset.available || 'false';
      item.dataset.inventory = option.dataset.inventory || '0';
      item.dataset.plans = option.dataset.plans || '';
      item.classList.toggle('is-soldout', item.dataset.available === 'false');
      this.applyStock([item]);
      if (this.included) this.setCount(item, item.dataset.stock === 'out' ? 0 : 1, true);
      this.update(false);
    }

    /* ------------------------------------------------------------ the page's buy button */

    /* The first cart form after the root in its section: the Buy Button a merchant places under the rows. */
    findForm() {
      const section = this.root.closest('.shopify-section') || document;
      const forms = Array.from(section.querySelectorAll('form[action*="/cart/add"]'));
      return forms.find((form) => this.root.compareDocumentPosition(form) & Node.DOCUMENT_POSITION_FOLLOWING) || forms[0] || null;
    }

    /* The section's other Buy Buttons that sell this product (a pre-order button under the first). */
    findOtherForms() {
      const section = this.root.closest('.shopify-section');
      if (!section) return [];
      return Array.from(section.querySelectorAll('product-form form[action*="/cart/add"]')).filter((form) => {
        if (form === this.form) return false;
        const id = form.querySelector('[name="id"]');
        return !!(id && this.variantsById.has(String(id.value)));
      });
    }

    /* The button's own text gives way to "below the minimum" text; dynamic checkout buys one line, a bundle is several. */
    prepareButton() {
      this.form = this.findForm();
      if (!this.form) return false;
      this.buttons = [this.form].concat(this.findOtherForms()).map((form) => {
        const submitBtn = form.querySelector('[type="submit"]');
        const submitText = submitBtn && submitBtn.querySelector('[data-submit-text]');
        let minText = null;
        if (submitText) {
          minText = document.createElement('span');
          minText.className = submitText.className;
          minText.hidden = true;
          submitText.insertAdjacentElement('afterend', minText);
        }
        const host = submitBtn && submitBtn.closest('[data-_sf]');
        const dc = host && host.querySelector('.sugar-product-buy-button__dc');
        if (dc) dc.hidden = true;
        return { form, submitBtn, submitText, minText };
      });
      ({ submitBtn: this.submitBtn, submitText: this.submitText, minText: this.minText } = this.buttons[0]);
      return true;
    }

    /* In a step Offers adds the rows; below the minimum the click never becomes a submit. */
    bindStepForm() {
      if (!this.prepareButton()) return;
      this.buttons.forEach(({ form }) => {
        this.listen(form, 'click', (e) => {
          if (this.total() >= this.min) return;
          if (!e.target.closest('[type="submit"]')) return;
          e.preventDefault();
          e.stopImmediatePropagation();
        }, true);
      });
    }

    bindForm() {
      if (!this.prepareButton()) return;
      this.buttons.forEach((button) => {
        this.listen(button.form, 'submit', (e) => {
          e.preventDefault();
          e.stopImmediatePropagation();
          if (this.total() < this.min) return;
          this.addToCart(button);
        }, true);
      });
    }

    syncForm(n, headerValues) {
      if (!this.buttons) return;
      const short = n < this.min;
      const minHtml = fill(this.buttonMinTpl, headerValues);
      this.buttons.forEach(({ submitBtn, submitText, minText }) => {
        if (!submitBtn) return;
        if (minText) {
          if (minText.innerHTML !== minHtml) minText.innerHTML = minHtml;
          minText.hidden = !short;
        }
        if (submitText) submitText.hidden = short;
        if (short) {
          submitBtn.setAttribute('aria-disabled', 'true');
          submitBtn.classList.add('is-sold-out');
        } else {
          submitBtn.removeAttribute('aria-disabled');
          submitBtn.classList.remove('is-sold-out');
        }
      });
    }

    bindDocument() {
      this.listen(document, 'variant:change', (e) => {
        const detail = e.detail || {};
        if (detail.sectionId && String(detail.sectionId) !== this.sectionId) return;
        const id = detail.variant && detail.variant.id;
        if (!id || !this.variantsById.has(String(id))) return;
        this.currentVariantId = String(id);
        this.update(false);
      });
      this.listen(document, 'offers:quantity-changed', (e) => {
        if (!this.offersMode || this.syncing || this.stepMode) return;
        const detail = e.detail || {};
        if (detail.sectionId && String(detail.sectionId) !== this.sectionId) return;
        const target = parseInt(detail.requestedQuantity, 10) || parseInt(detail.quantity, 10) || 0;
        if (target > 0 && target !== this.total()) this.fillFromOffers(false, target);
      });
      /* The step opened: the count picked in step 1 is the minimum, and the offer stays at least there. */
      this.listen(document, 'step-two:open', (e) => {
        const detail = e.detail || {};
        if (!this.stepMode || detail.root !== this.stepRoot) return;
        const count = parseInt(detail.count, 10) || 1;
        this.min = Math.max(this.baseMin, count);
        if (this.max && this.min > this.max) this.max = this.min;
        if (this.unitsMode) this.setUnits(count);
        this.update(false);
        if (this.offersMode && this.offersQuantity() !== Math.max(this.total(), this.min)) this.syncToOffers();
      });
      this.listen(document, 'offers:prices-updated', (e) => {
        if (!this.unitsMode || !this.addUnitBtn) return;
        const detail = e.detail || {};
        if (detail.sectionId && String(detail.sectionId) !== this.sectionId) return;
        this.paintAddUnit(this.total());
      });
      this.listen(document, 'subscription:type-changed', (e) => {
        const detail = e.detail || {};
        if (detail.sectionId && String(detail.sectionId) !== this.sectionId) return;
        this.pagePlanId = detail.type === 'subscribe' && detail.sellingPlanId ? String(detail.sellingPlanId) : null;
        this.update(false);
      });
    }

    /* ------------------------------------------------------------ cart */

    /* A Buy Button other than the first, with a Subscription plan of its own (a pre-order plan), adds on that plan */
    ownPlanOf(form) {
      if (!form || form === this.form) return null;
      const input = form.querySelector('input[name="selling_plan"]');
      return input && !input.disabled && input.value ? String(input.value) : null;
    }

    /* What the picked rows cost when `form` adds them on `planId`, by the plan rule cartItems uses;
       null when that form adds on another plan (sugar-pricing.js, a pre-order button's price) */
    ownPlanTotal(form, planId) {
      const n = this.total();
      const active = this.activeIndex(n);
      const tier = active >= 0 ? this.tiers[active] : null;
      const plan = this.ownPlanOf(form) || (tier && tier.planId) || this.pagePlanId;
      if (!(n > 0) || !plan || String(plan) !== String(planId)) return null;
      let total = 0;
      this.items().forEach((item) => {
        const qty = this.countOf(item);
        if (qty) total += this.unitPrice(this.variantOf(item), tier, true, String(planId)) * qty;
      });
      return { quantity: n, totalPrice: total };
    }

    cartItems(form) {
      const n = this.total();
      const active = this.activeIndex(n);
      const tier = active >= 0 ? this.tiers[active] : null;
      const planId = this.ownPlanOf(form) || (tier && tier.planId) || this.pagePlanId;
      const lines = [];
      this.items().forEach((item) => {
        const qty = this.countOf(item);
        if (!qty) return;
        const variant = this.variantOf(item);
        const id = variant ? parseInt(variant.id, 10) : parseInt(item.dataset.variantId, 10);
        if (!id) return;
        const line = { id, quantity: qty };
        if (planId) line.selling_plan = parseInt(planId, 10);
        lines.push(line);
      });
      return lines;
    }

    showError(message) {
      if (!this.errorEl) return;
      this.errorEl.textContent = message || '';
      this.errorEl.hidden = !message;
    }

    async addToCart(button = this.buttons && this.buttons[0]) {
      const submitBtn = button && button.submitBtn;
      if (!submitBtn || this.total() < this.min) return;
      const items = this.cartItems(button.form);
      if (!items.length) return;
      // Lines other blocks queued for this buy (upsells, gifts)
      const extras = window.SugarCart && window.SugarCart.extras ? window.SugarCart.extras.collect(submitBtn) : [];
      if (extras.length) items.push(...extras);
      this.showError('');
      const spinner = submitBtn.querySelector('.loading__spinner');
      submitBtn.setAttribute('aria-disabled', 'true');
      submitBtn.classList.add('loading');
      if (spinner) spinner.classList.remove('hidden');
      try {
        const drawer = document.querySelector('sugar-cart-drawer');
        const sections = drawer && typeof drawer.getSectionsToRender === 'function'
          ? drawer.getSectionsToRender().map((section) => section.id)
          : undefined;
        const res = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(sections ? { items, sections, sections_url: window.location.pathname } : { items })
        });
        const data = await res.json();
        if (data.status) throw new Error(data.description || 'Unable to add items to cart.');
        if (extras.length) window.SugarCart.extras.commit(submitBtn, data);
        if (drawer) {
          const cartData = window.SugarCart ? await window.SugarCart.get({ fresh: true }) : null;
          if (cartData && typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
            publish(PUB_SUB_EVENTS.cartUpdate, { source: 'bundle-builder', cartData, sections: data.sections });
          }
          if (typeof drawer.renderContents === 'function') drawer.renderContents(data);
          else drawer.open();
          document.dispatchEvent(new CustomEvent('cart:update', { detail: data }));
        } else {
          window.location.href = '/cart';
        }
      } catch (err) {
        this.showError(err.message || 'Unable to add items to cart.');
      } finally {
        submitBtn.removeAttribute('aria-disabled');
        submitBtn.classList.remove('loading');
        if (spinner) spinner.classList.add('hidden');
      }
    }
  }

  /* ---------------------------------------------------------------- boot */

  const instances = new WeakMap();

  function init(root) {
    if (!root || instances.has(root)) return instances.get(root);
    const builder = new BundleBuilder(root);
    instances.set(root, builder);
    return builder;
  }

  function initAll(scope) {
    (scope || document).querySelectorAll('[data-bundle-builder]').forEach((root) => init(root));
  }

  window.SugarBundleBuilder = { init, initAll, get: (root) => instances.get(root) || null };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initAll());
  else initAll();

  document.addEventListener('shopify:section:load', (e) => initAll(e.target));
  document.addEventListener('shopify:block:select', (e) => {
    const root = e.target && e.target.closest && e.target.closest('[data-bundle-builder]');
    if (root && !instances.has(root)) init(root);
  });
})();
